// Session bootstrap for the authenticated app shell.
import { getSupabase } from '../api/client.js';
import { fetchMyMemberships, fetchMyPermissions, fetchMyProfile, recordSessionEvent } from '../api/session.api.js';
import { createPermissionSet } from '../permissions/permissions.js';
import { setState, getState } from '../state.js';
import { getPref, setPref } from '../utilities/storage.js';
import { setDisplayTimezone } from '../utilities/dates.js';
import { applyTheme, currentTheme } from '../services/theme.js';

export const LOGIN_PATH = '/login.html';

export function redirectToLogin(reason) {
  const next = window.location.pathname + window.location.search;
  const params = new URLSearchParams();
  if (next && next !== '/') params.set('next', next);
  if (reason) params.set('reason', reason);
  const qs = params.toString();
  window.location.replace(LOGIN_PATH + (qs ? `?${qs}` : ''));
}

/** Only allow same-app relative paths as post-login destinations (prevents open redirects). */
export function safeNextPath(value) {
  if (!value || typeof value !== 'string') return '/';
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return '/';
  if (/^\/(login|reset-password|accept-invite)\.html/.test(value)) return '/';
  return value;
}

export async function currentAal() {
  const { data } = await getSupabase().auth.mfa.getAuthenticatorAssuranceLevel();
  return { current: data?.currentLevel ?? 'aal1', next: data?.nextLevel ?? 'aal1' };
}

/**
 * Loads everything the shell needs. Returns one of:
 *  { status: 'signed_out' } | { status: 'no_membership' } | { status: 'mfa_challenge' } | { status: 'ready' }
 */
export async function bootstrapSession() {
  const sb = getSupabase();
  const { data: { session } } = await sb.auth.getSession();
  if (!session) return { status: 'signed_out' };

  const { data: { user }, error: userError } = await sb.auth.getUser(); // validates the token with the server
  if (userError || !user) return { status: 'signed_out' };

  const [profile, memberships] = await Promise.all([fetchMyProfile(user.id), fetchMyMemberships()]);
  const active = memberships.filter((m) => m.status === 'active');
  setState({ user, profile, memberships });
  if (!active.length) return { status: 'no_membership' };

  const preferred = getPref('org');
  const membership = active.find((m) => m.org_id === preferred) ?? active[0];
  setPref('org', membership.org_id);
  setDisplayTimezone(membership.org_timezone);

  // Profile theme wins over the local preference once known (keeps devices consistent).
  if (profile?.theme && profile.theme !== currentTheme()) applyTheme(profile.theme);

  const aal = await currentAal();
  if (membership.mfa_required && aal.current !== 'aal2') {
    if (aal.next === 'aal2') return { status: 'mfa_challenge' };   // has a factor: verify it
    setState({ mfaEnrollmentRequired: true });                     // no factor yet: must enroll
  }

  const permissions = createPermissionSet(await fetchMyPermissions(membership.org_id));
  setState({ membership, permissions, aal: aal.current });
  return { status: 'ready' };
}

export async function refreshPermissions() {
  const { membership } = getState();
  if (!membership) return;
  setState({ permissions: createPermissionSet(await fetchMyPermissions(membership.org_id)) });
}

export async function signOut({ everywhere = false, reason } = {}) {
  await recordSessionEvent(reason === 'idle' ? 'idle_timeout' : 'logout');
  try { await getSupabase().auth.signOut({ scope: everywhere ? 'global' : 'local' }); } catch { /* still leave */ }
  window.location.replace(LOGIN_PATH + (reason ? `?reason=${encodeURIComponent(reason)}` : ''));
}
